/**
 * Moving an attachment's bytes over `/api/v1/attachments/:entityId`. REST rather than GraphQL
 * only for the transfer itself: bytes never cross as base64 inside JSON (see constitution).
 * Listing and removing attachments are GraphQL operations.
 */
import { getConfig } from '@/config';
import { filenameFromContentDisposition } from '@/utils/content-disposition';

/** A downloaded attachment: the bytes, with what names and types them. */
export interface AttachmentContent {
  readonly filename: string;
  readonly mimeType: string;
  readonly blob: Blob;
}

/** The backend refused the file itself (name, extension, or content that belies it). */
export const ATTACHMENT_REFUSED = 'ATTACHMENT_REFUSED';

/** The document no longer accepts a change to its attachments. */
export const ATTACHMENT_DOCUMENT_LOCKED = 'ATTACHMENT_DOCUMENT_LOCKED';

/** The file is larger than the backend stores. */
export const ATTACHMENT_TOO_LARGE = 'ATTACHMENT_TOO_LARGE';

/** The server cannot take the file right now (rate limit or checker capacity); retry later. */
export const ATTACHMENT_BUSY = 'ATTACHMENT_BUSY';

/** The body did not arrive within the server's deadline: the connection is too slow. */
export const ATTACHMENT_TOO_SLOW = 'ATTACHMENT_TOO_SLOW';

/** The document already carries as many files as it may. */
export const ATTACHMENT_LIMIT_REACHED = 'ATTACHMENT_LIMIT_REACHED';

const attachmentsUrl = (entityNodeId: string, fileId?: string): string => {
  const base = `${getConfig().apiBaseUrl}/v1/attachments/${encodeURIComponent(entityNodeId)}`;
  return fileId ? `${base}/${encodeURIComponent(fileId)}` : base;
};

/**
 * Turns a refusal into the sentinel the caller localises.
 * Two different 409s share the status, so the body is what tells them apart.
 */
async function refusal(response: Response): Promise<Error> {
  const body = await response.json().catch(() => ({}));
  if (body?.errorCode === ATTACHMENT_LIMIT_REACHED) return new Error(ATTACHMENT_LIMIT_REACHED);
  if (response.status === 409) return new Error(ATTACHMENT_DOCUMENT_LOCKED);
  if (response.status === 413) return new Error(ATTACHMENT_TOO_LARGE);
  if (response.status === 400) return new Error(ATTACHMENT_REFUSED);
  if (response.status === 429 || response.status === 503) return new Error(ATTACHMENT_BUSY);
  if (response.status === 408) return new Error(ATTACHMENT_TOO_SLOW);
  return new Error(`Attachment request failed with ${response.status}`);
}

/**
 * Sends one file to the document named by `entityNodeId`.
 *
 * @throws Error whose message is one of the sentinels above, so the caller can localise it.
 */
export async function uploadAttachment(entityNodeId: string, file: File): Promise<void> {
  const body = new FormData();
  body.append('file', file);

  const response = await fetch(attachmentsUrl(entityNodeId), {
    method: 'POST',
    body,
    credentials: 'include'
  });
  if (!response.ok) throw await refusal(response);
}

/**
 * Where the browser saves an attachment from: a plain link, so the bytes never pass through JS.
 * Assumes a same-origin API; cross-origin, a failed download replaces the page with its error.
 */
export function attachmentDownloadUrl(entityNodeId: string, fileId: string): string {
  return attachmentsUrl(entityNodeId, fileId);
}

/**
 * The attachment's bytes, for rendering them in the page (e.g. an e-mail body preview).
 * Saving a file is a link to `attachmentDownloadUrl` instead.
 */
export async function downloadAttachment(
  entityNodeId: string,
  fileId: string
): Promise<AttachmentContent> {
  const response = await fetch(attachmentsUrl(entityNodeId, fileId), { credentials: 'include' });
  if (!response.ok) throw await refusal(response);

  return {
    // The stored name, from the `filename*` the backend always sends alongside the plain one.
    filename:
      filenameFromContentDisposition(response.headers.get('content-disposition')) ?? 'attachment',
    mimeType: response.headers.get('content-type') ?? 'application/octet-stream',
    blob: await response.blob()
  };
}
